import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { motion } from 'motion/react'

import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import Button from '../ui/buttons/Button'
import TextHeadline from '../ui/texts/TextHeadline'

type InactivityModalProps = {
    countdownSeconds: number
    onContinue: () => void
    onEnd: () => void
}

const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2, ease: 'easeOut' as const } },
    exit: { opacity: 0, transition: { duration: 0.2, ease: 'easeIn' as const } },
}

const cardVariants = {
    hidden: { opacity: 0, scale: 0.8, y: -20 },
    visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
            type: 'spring' as const,
            stiffness: 380,
            damping: 18,
            mass: 0.8,
        },
    },
    exit: { opacity: 0, scale: 0.95, transition: { duration: 0.15, ease: 'easeIn' as const } },
}

export default function InactivityModal({
    countdownSeconds,
    onContinue,
    onEnd,
}: InactivityModalProps) {
    const isStele = useSteleBreakpoint()
    const [secondsLeft, setSecondsLeft] = useState(countdownSeconds)

    useEffect(() => {
        if (secondsLeft <= 0) {
            onEnd()
            return
        }

        const timeoutId = window.setTimeout(() => {
            setSecondsLeft((previous) => previous - 1)
        }, 1000)

        return () => window.clearTimeout(timeoutId)
    }, [secondsLeft, onEnd])

    return (
        <motion.div
            className='z-[10000] fixed inset-0 flex justify-center items-end bg-black/55 backdrop-blur-sm pointer-events-auto'
            variants={overlayVariants}
            initial='hidden'
            animate='visible'
            exit='exit'
        >
            <motion.div
                className={clsx(
                    'absolute flex flex-col justify-center items-center drop-shadow-2-black-stroke bg-white-stroke-even-darker border-black-stroke text-center pattern-background',
                    'gap-4 px-8 pt-12 pb-8 border-2 rounded-[48px] max-w-110 bottom-32',
                    'stele:gap-8 stele:px-16 stele:pt-24 stele:pb-16 stele:border-4 stele:rounded-[128px] stele:max-w-360 stele:min-w-360 stele:bottom-128',
                )}
                variants={cardVariants}
            >
                <div className='flex flex-col items-center'>
                    <TextHeadline
                        className={clsx(
                            'absolute',
                            '-top-7',
                            'stele:-top-35',
                        )}
                        fontSize={isStele ? 160 : 56}
                        leading={1}
                        tracking={'-2%'}
                        textColor='#DD0B2F'
                        outlineColor='#8A0B00'
                        dropShadowColor='#8A0B00'
                    >
                        {String(Math.max(0, secondsLeft))}
                    </TextHeadline>
                </div>
                <p
                    className={clsx(
                        'text-white-stroke-lightest font-nunito font-bold text-center tracking-[-2%]',
                        'text-[18px] leading-6 max-w-72',
                        'stele:text-[64px] stele:leading-22 stele:max-w-none',
                    )}
                    style={{ textShadow: '0 2px 0 #06487C' }}
                >
                    Spielst du noch?<br></br>
                    Der Countdown läuft!
                </p>
                <div
                    className={clsx(
                        'flex justify-center items-stretch w-full',
                        'gap-3',
                        'stele:gap-8',
                    )}
                >
                    <div className='flex-1'>
                        <Button type='button' color='blue' onClick={onContinue}>
                            Fortsetzen
                        </Button>
                    </div>
                    <div className='flex-1'>
                        <Button type='button' color='red' onClick={onEnd}>
                            Beenden
                        </Button>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    )
}

